import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

import { marketingPalette } from "@/components/marketing/tokens";
import NavAnchor from "./NavAnchor";
import type { CompactLink, FeatureLink, PlainLink } from "./navigation";
import { useSummary } from "./headerCounts";
import {
  COMPACT_TONES,
  ICON_TONES,
  PRICE_TINT,
  ROW_HOVER_MS,
  ROW_TINT,
} from "./tokens";

/** Present on the wide panel, absent in the drawer, which repaints nothing. */
const TINT = "MarketingHeader-rowTint";

export type RowVariant = "panel" | "drawer";

/**
 * Every place the drawer disagrees with the panel, in one table each. The two
 * are different enough, the drawer drops the summary, the price caption and
 * the hover tint, and repacks what is left, that reading them side by side is
 * the only way to see what actually changes.
 */
const FEATURE_ROW = {
  panel: {
    fontSize: 15,
    [`&:hover .${TINT}`]: { opacity: 1, transform: "none" },
  },
  drawer: {
    fontSize: 14,
    p: "4.8px",
    borderRadius: "8px",
    backgroundColor: alpha(marketingPalette.wash, 0.6),
  },
};

const FEATURE_LEFT = { panel: {}, drawer: { alignItems: "center" } };

const PRICE_BOX = {
  panel: { alignItems: "flex-start", minWidth: "99.2px", p: "6.08px" },
  drawer: { alignItems: "flex-end" },
};

const PRICE_PILL = {
  panel: { p: "2.08px 8px", backgroundColor: PRICE_TINT, fontWeight: 500 },
  drawer: { p: "6.4px", backgroundColor: "common.white", fontWeight: 400 },
};

const COMPACT_PILL = {
  panel: { p: "2.08px 8px", fontWeight: 500 },
  drawer: { p: "6.4px", fontWeight: 400 },
};

/** A compact row packs tighter in the drawer, art included. */
const COMPACT_ROW = { panel: { p: "12.8px" }, drawer: { p: "4.8px" } };
const COMPACT_ICON = { panel: 20, drawer: 16 };

const PLAIN_ROW = {
  panel: { p: "8px 12px", backgroundColor: ROW_TINT, fontSize: 15 },
  drawer: {
    p: "8px",
    backgroundColor: alpha(marketingPalette.wash, 0.6),
    fontSize: 14,
  },
};

const ICON_SQUARE = {
  plain: { flex: "0 0 auto", width: 35.19, height: 35.19 },
  tinted: { flex: "0 1 auto", width: 38.08, height: 38.08 },
};

/**
 * The drawer draws one 20px square whatever the tone, with the art at 16px and
 * no padding between the two. Carrying the panel's square here costs 12px on
 * every feature row, which is most of a menu.
 */
const DRAWER_SQUARE = { flex: "0 0 auto", width: 20, height: 20 };

/**
 * The uppercase label that names a column, or a block inside one. `tight` is
 * the drawer's column label, which sits half a row gap above its first row
 * rather than the full one the stack would otherwise give it.
 */
export function CapsHeading({
  children,
  tight,
}: {
  children: string;
  tight?: true;
}) {
  return (
    <Typography
      component="p"
      sx={{
        ...(tight ? { mb: "-4.8px" } : undefined),
        fontSize: 12.96,
        fontWeight: 500,
        lineHeight: "20.96px",
        letterSpacing: "0.48px",
        textTransform: "uppercase",
        color: marketingPalette.ink,
      }}
    >
      {children}
    </Typography>
  );
}

function IconSquare({
  icon,
  tone,
  variant,
}: {
  icon: string;
  tone: FeatureLink["tone"];
  variant: RowVariant;
}) {
  const drawer = variant === "drawer";
  return (
    <Box
      sx={{
        ...(drawer
          ? DRAWER_SQUARE
          : ICON_SQUARE[tone === "plain" ? "plain" : "tinted"]),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: drawer ? 0 : "6.08px",
        borderRadius: "8px",
        backgroundColor: ICON_TONES[tone],
        // A hovered row repaints only this square.
        transition: `background-color ${ROW_HOVER_MS}ms ease`,
      }}
    >
      <Box
        component="img"
        src={icon}
        alt=""
        sx={
          drawer
            ? { width: "16px", height: "16px" }
            : { width: 1, maxWidth: 1, height: "26.08px" }
        }
      />
    </Box>
  );
}

/**
 * The tint a hovered row wears. It is a sibling rather than a background
 * because the published one bleeds 8px past the row on every side and grows in
 * from 110%, which no background on the row itself can do.
 */
function RowTint() {
  return (
    <Box
      className={TINT}
      sx={{
        position: "absolute",
        inset: "-8px",
        borderRadius: "16px",
        backgroundColor: ROW_TINT,
        opacity: 0,
        transform: "scale(1.1)",
        transition: `opacity ${ROW_HOVER_MS}ms ease, transform ${ROW_HOVER_MS}ms ease`,
      }}
    />
  );
}

/** The right-hand price block, which only the Products menu quotes. */
function PriceBlock({
  price,
  variant,
}: {
  price: NonNullable<FeatureLink["price"]>;
  variant: RowVariant;
}) {
  return (
    <Box
      sx={{
        ...PRICE_BOX[variant],
        display: "flex",
        flexDirection: "column",
        borderRadius: "8px",
        backgroundColor: "common.white",
      }}
    >
      {variant === "panel" ? (
        <Typography
          component="p"
          sx={{
            fontSize: 11.04,
            lineHeight: "20px",
            letterSpacing: "0.64px",
            textTransform: "uppercase",
            color: marketingPalette.inkSubtle,
          }}
        >
          {price.caption}
        </Typography>
      ) : null}
      <Box
        sx={{
          ...PRICE_PILL[variant],
          display: "flex",
          gap: "3.2px",
          borderRadius: "8px",
          fontSize: 12.48,
          lineHeight: "17.472px",
        }}
      >
        {price.value}
      </Box>
    </Box>
  );
}

function FeatureText({
  link,
  variant,
}: {
  link: FeatureLink;
  variant: RowVariant;
}) {
  const summary = useSummary(link);
  return (
    <Box>
      <Typography
        component="p"
        sx={{
          fontSize: "inherit",
          lineHeight: "22px",
          color: "inherit",
        }}
      >
        {link.label}
      </Typography>
      {variant === "panel" ? (
        <Typography
          component="p"
          sx={{
            fontSize: 12.64,
            lineHeight: "21px",
            color: alpha(marketingPalette.inkSubtle, 0.71),
          }}
        >
          {summary}
        </Typography>
      ) : null}
    </Box>
  );
}

function FeatureBody({
  link,
  variant,
}: {
  link: FeatureLink;
  variant: RowVariant;
}) {
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box
        sx={{ ...FEATURE_LEFT[variant], display: "flex", columnGap: "10.08px" }}
      >
        <Box sx={{ py: "2.08px", flex: "0 0 auto" }}>
          <IconSquare icon={link.icon} tone={link.tone} variant={variant} />
        </Box>
        <FeatureText link={link} variant={variant} />
      </Box>
      {link.price ? <PriceBlock price={link.price} variant={variant} /> : null}
    </Box>
  );
}

/** A menu's headline entry: icon, title, and, on the panel, a summary line. */
export function FeatureRow({
  link,
  variant,
  onNavigate,
}: {
  link: FeatureLink;
  variant: RowVariant;
  onNavigate?: () => void;
}) {
  return (
    <NavAnchor
      href={link.href}
      onNavigate={onNavigate}
      sx={{
        ...FEATURE_ROW[variant],
        position: "relative",
        display: "block",
        width: 1,
        maxWidth: 1,
        lineHeight: "22px",
      }}
    >
      {variant === "panel" ? <RowTint /> : null}
      <FeatureBody link={link} variant={variant} />
    </NavAnchor>
  );
}

/** The tinted premium rows under each Products column. */
export function CompactRow({
  link,
  variant,
  onNavigate,
}: {
  link: CompactLink;
  variant: RowVariant;
  onNavigate?: () => void;
}) {
  const tone = COMPACT_TONES[link.tone];
  return (
    <NavAnchor
      href={link.href}
      onNavigate={onNavigate}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        maxWidth: 1,
        gap: 2,
        ...COMPACT_ROW[variant],
        borderRadius: "8px",
        border: `1px solid ${tone.fill}`,
        backgroundColor: tone.fill,
        transition: `border-color ${ROW_HOVER_MS}ms ease`,
        "&:hover": { borderColor: tone.hover },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          component="img"
          src={link.icon}
          alt=""
          sx={{ width: COMPACT_ICON[variant], height: COMPACT_ICON[variant] }}
        />
        <Typography component="span" sx={{ fontSize: 14, lineHeight: "21px" }}>
          {link.label}
        </Typography>
      </Box>
      <Box
        sx={{
          ...COMPACT_PILL[variant],
          borderRadius: "8px",
          backgroundColor: "common.white",
          fontSize: 12.96,
          lineHeight: "18.144px",
        }}
      >
        {link.price}
      </Box>
    </NavAnchor>
  );
}

/** The tag at the end of a row new enough to point out, on the panel's price chip. */
function Badge({ children }: { children: string }) {
  return (
    <Box
      component="span"
      sx={{
        flex: "0 0 auto",
        p: "2.08px 8px",
        borderRadius: "8px",
        backgroundColor: "common.white",
        fontSize: 12.48,
        fontWeight: 500,
        lineHeight: "17.472px",
      }}
    >
      {children}
    </Box>
  );
}

/** The icon-and-label rows the Specialty column is made of. */
export function PlainRow({
  link,
  variant,
  onNavigate,
}: {
  link: PlainLink;
  variant: RowVariant;
  onNavigate?: () => void;
}) {
  const label = (
    <Typography
      component="p"
      sx={{ fontSize: "inherit", lineHeight: "22px", color: "inherit" }}
    >
      {link.label}
    </Typography>
  );
  return (
    <NavAnchor
      href={link.href}
      onNavigate={onNavigate}
      sx={{
        ...PLAIN_ROW[variant],
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        maxWidth: 1,
        gap: 2,
        borderRadius: "8px",
        border: `1px solid ${ROW_TINT}`,
        lineHeight: "22px",
        transition: `border-color ${ROW_HOVER_MS}ms ease`,
        "&:hover": { borderColor: alpha(marketingPalette.ink, 0.68) },
      }}
    >
      <Box
        component="img"
        src={link.icon}
        alt=""
        sx={{ width: 20, height: 20 }}
      />
      {link.badge ? (
        // No gap before the tag: two lists side by side leave the row 2px to spare.
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {label}
          <Badge>{link.badge}</Badge>
        </Box>
      ) : (
        label
      )}
    </NavAnchor>
  );
}
